<template>
    <div class="">
        <table class="trims-table">
            <thead>
            <tr>
                <th class="th-trim">
                    <SearchInput
                            :placeholder="'  Trim name'"
                            @search="searchByTrimName"
                    ></SearchInput>
                </th>
                <th class="th-description">
                    <SearchInput
                            :placeholder="'  Description'"
                            @search="searchByDescription"
                    ></SearchInput>
                </th>
                <th class="th-email">Created by</th>
                <th class="th-date">Date created</th>
                <th class="th-email">Exported by</th>
                <th class="th-date">Date exported</th>
                <th class="th-status">Status</th>
                <th class="th-buttons"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="trim in trims" :key="trim.id">
                <td class="no-wrap" :title="trim.trimName">
                    <router-link :to="trimEditorUrl(trim.id, trim.trimName)">
                        {{ trim.trimName }}
                        <span v-if="isCustomerUser && trim.customer_id" class="customer-indicator" title="Your Design"> ★</span>
                    </router-link>
                </td>
                <td :title="trim.description">{{ trim.description }}</td>
                <td class="email-col" :title="trim.created_by_email || 'No email'">{{ formatEmail(trim.created_by_email) }}</td>
                <td class="no-wrap">{{ dateFormat(trim.date_created) }}</td>
                <td class="email-col" :title="trim.exported_by_email || 'No email'">{{ formatEmail(trim.exported_by_email) }}</td>
                <td class="no-wrap">{{ dateFormat(trim.date_exported) }}</td>
                <td class="no-wrap" :class="{'color-green': trim.exported}">{{ trimStatus(trim.exported) }}</td>
                <td class="no-wrap">
                    <template v-if="!trim.exported && hasUserAccess">
                        <button class="btn" title="Edit info" @click="openEditInfoModal(trim)"><i class="icon-pencil"></i></button>
                        <button class="btn btn-delete" title="Delete trim" @click="openDeleteTrimModal(trim)"><i class="icon-trash-empty"></i></button>
                    </template>
                </td>
            </tr>
            <tr v-if="showEmptyState">
                <td colspan="8" class="empty-state">
                    <div class="empty-state-content">
                        <div v-if="isCustomerUser" class="customer-empty-state">
                            <h3>No Trim Designs Found</h3>
                            <p v-if="customerTrimCount === 0">You haven't created any trim designs yet.</p>
                            <p v-else-if="customerTrimCount > 0">No trims match your current search criteria.</p>
                            <p><strong>Total designs:</strong> {{ customerTrimCount || 0 }}</p>
                        </div>
                        <div v-else class="non-customer-empty-state">
                            <h3>Access Required</h3>
                            <p>To view and create trim designs, please access through your customer portal.</p>
                            <p>Contact support if you need assistance accessing your designs.</p>
                        </div>
                    </div>
                </td>
            </tr>
            </tbody>
        </table>
        
        <EditTrimInfoModal v-if="displayEditInfoModal"
                           :trim="trimToEditInfo"
                           @save="trimEdited"
                           @closeModal="closeEditInfoModal"
        ></EditTrimInfoModal>

        <DeleteTrimModal v-if="displayDeleteModal"
                         :trim="trimToDelete"
                         @closeModal="closeDeleteTrimModal"
        ></DeleteTrimModal>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex';
    import SearchInput from "../../components/SearchInput";
    import DeleteTrimModal from "../../components/modals/DeleteTrimModal";
    import EditTrimInfoModal from "../../components/modals/EditTrimInfoModal";

    export default {
        name: "TrimsTable",

        components: {
            EditTrimInfoModal,
            DeleteTrimModal,
            SearchInput,
        },

        emits: [
            'search',
            'trimEdited',
        ],

        props: [
            'trims',
            'dataFetching',
        ],

        data() {
            return {
                searchTrimName: '',
                searchDescription: '',

                displayDeleteModal: false,
                trimToDelete: null,

                displayEditInfoModal: false,
                trimToEditInfo: null,
            }
        },

        computed: {
            ...mapGetters('trims', [
                'getTrimNameAsUrl',
            ]),

            ...mapGetters('users', [
                'userPageUrl',
            ]),

            ...mapGetters('authentication', [
                'hasUserAccess',
                'hasAdminAccess',
                'hasCustomerContext',
            ]),

            // Get customer trim count from trims store
            customerTrimCount() {
                return this.$store.state.trims.customer_trim_count;
            },

            // Check if current user is a customer user
            isCustomerUser() {
                return this.hasCustomerContext;
            },

            // Show empty state when no data and not fetching
            showEmptyState() {
                return !this.dataFetching && !this.trims.length;
            },

            searchQuery() {
                let trimName = this.searchTrimName;
                let description = this.searchDescription;

                return {
                    trimName,
                    description,
                }
            },

        },

        methods: {
            ...mapActions('trims', [
                'searchTrim',
            ]),

            search() {
                this.$emit('search', {searchQuery: this.searchQuery, page: null});
            },

            searchByTrimName(value) {
                this.searchTrimName = value;

                this.search();
            },

            searchByDescription(value) {
                this.searchDescription = value;

                this.search();
            },

            openEditInfoModal(trim) {
                this.trimToEditInfo = trim;

                this.displayEditInfoModal = true;
            },

            closeEditInfoModal() {
                // clear variable
                this.trimToEditInfo = null;

                this.displayEditInfoModal = false;
            },

            openDeleteTrimModal(trim) {
                this.trimToDelete = trim;

                this.displayDeleteModal = true;
            },

            closeDeleteTrimModal() {
                // clear variable
                this.trimToDelete = null;

                this.displayDeleteModal = false;
            },

            trimEditorUrl(trimId, trimName) {
                // for use the trimId and trimName in the route path is need to convert it in the url format
                const trimNameAsUrl = this.getTrimNameAsUrl(trimId, trimName);

                return `/editor/${trimNameAsUrl}`;
            },

            dateFormat(date) {
                if (!date) return '-';
                
                try {
                    // Create a Date object from the input date
                    const dateObj = new Date(date);
                    
                    // Check if the date is valid
                    if (isNaN(dateObj.getTime())) {
                        return '-';
                    }
                    
                    // Format as MM-DD-YYYY
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    const year = dateObj.getFullYear();
                    
                    return `${month}-${day}-${year}`;
                } catch (error) {
                    console.warn('Error formatting date:', date, error);
                    return '-';
                }
            },

            trimStatus(isExported) {
                return isExported ? 'exported' : 'draft';
            },

            getUserName(userData) {
                if (!userData) return '';

                let userName = userData[1];

                return userName || '';
            },

            linkToUserPage(userData) {
                const [id, name] = userData;

                return this.userPageUrl(name, id);
            },

            displayLinkToUserPage(userData) {
                return this.hasAdminAccess && userData?.id;
            },

            trimEdited(trimInfo) {
                // using in the trimInfo modal
                this.$emit('trimEdited', trimInfo);
            },

            formatEmail(email) {
                if (!email) return '-';
                // Truncate long emails but show full email in tooltip
                return email.length > 25 ? email.substring(0, 22) + '...' : email;
            },

        },

    }
</script>

<style scoped>
    .th-trim {
        width: 160px;
        min-width: 160px;
        max-width: 160px;
    }

    .th-description {
        min-width: 140px;
    }

    .th-user {
        width: 150px;
        min-width: 150px;
    }

    .th-email {
        width: 200px;
        min-width: 200px;
        max-width: 200px;
    }

    .th-date {
        width: 120px;
        min-width: 120px;
        max-width: 120px;
    }

    .th-status {
        width: 90px;
        min-width: 90px;
        max-width: 90px;
    }

    .th-buttons {
        width: 95px;
        min-width: 95px;
        max-width: 95px;
    }

    .trims-table tr:not(:hover) .btn {
        color: transparent;
    }

    .btn {
        padding: 0 2px;
        border: none;
        font-size: 16px;
        line-height: 1;
        text-align: center;
        color: #aaa;
        background: none;
        cursor: pointer;
    }

    .btn:hover {
        color: #000;
    }

    .btn-delete:hover {
        color: #ad0100;
    }

    .customer-indicator {
        color: #ffd700;
        font-weight: bold;
        margin-left: 5px;
    }

    .empty-state {
        text-align: center;
        padding: 40px 20px;
    }

    .empty-state-content {
        max-width: 500px;
        margin: 0 auto;
    }

    .customer-empty-state h3,
    .non-customer-empty-state h3 {
        color: #333;
        margin-bottom: 15px;
    }

    .customer-empty-state p,
    .non-customer-empty-state p {
        color: #666;
        margin-bottom: 10px;
        line-height: 1.5;
    }

    .non-customer-empty-state {
        color: #888;
    }

    .email-col {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 200px;
    }

</style>